import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from '../services/poiService';
import { GripVertical, X, Save, RotateCcw, Image, Music, Video, Type } from 'lucide-react';

interface CardOrderManagerProps {
  cards: Card[];
  onSave: (orderedCardIds: string[]) => void;
  onCancel: () => void;
}

interface SortableCardItemProps {
  card: Card;
  index: number;
}

const SortableCardItem: React.FC<SortableCardItemProps> = ({ card, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id_v2 });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getContentTypeIcon = () => {
    if (card.video_url) return <Video className="w-4 h-4 text-purple-500" />;
    if (card.audio_url) return <Music className="w-4 h-4 text-blue-500" />;
    if (card.image_url) return <Image className="w-4 h-4 text-green-500" />;
    return <Type className="w-4 h-4 text-gray-500" />;
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getCardPreview = () => {
    if (card.text) {
      const plainText = stripHtml(card.text);
      return plainText.substring(0, 100) + (plainText.length > 100 ? '...' : '');
    }
    return 'No text content';
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-3 ${
        isDragging ? 'shadow-2xl ring-2 ring-primary-500' : 'shadow-sm'
      }`}
    >
      <div className="flex items-start space-x-3">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing mt-1 touch-none"
          title="Drag to reorder"
        >
          <GripVertical className="w-5 h-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
        </div>

        {/* Position Number */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 flex items-center justify-center font-semibold text-sm">
          {index + 1}
        </div>

        {/* Card Preview */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            {getContentTypeIcon()}
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {card.language_code || 'No language'}
            </span>
            {card.validation_status === 'PENDING' && (
              <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full">
                Pending
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
            {getCardPreview()}
          </p>
          {card.image_url && (
            <div className="mt-2">
              <img
                src={card.image_url}
                alt="Card preview"
                className="w-full h-20 object-cover rounded"
                loading="lazy"
                crossOrigin="anonymous"
                referrerPolicy="strict-origin-when-cross-origin"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CardOrderManager: React.FC<CardOrderManagerProps> = ({ cards, onSave, onCancel }) => {
  const [orderedCards, setOrderedCards] = useState<Card[]>(cards);
  const [hasChanges, setHasChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px of movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrderedCards((items) => {
        const oldIndex = items.findIndex((item) => item.id_v2 === active.id);
        const newIndex = items.findIndex((item) => item.id_v2 === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        setHasChanges(true);
        return newOrder;
      });
    }
  };

  const handleReset = () => {
    if (hasChanges && !window.confirm('Are you sure you want to reset the order? All changes will be lost.')) {
      return;
    }
    setOrderedCards(cards);
    setHasChanges(false);
  };

  const handleSave = () => {
    const orderedIds = orderedCards.map((card) => card.id_v2);
    onSave(orderedIds);
  };

  const handleCancel = () => {
    if (hasChanges && !window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
      return;
    }
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Reorder Cards
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Drag and drop cards to change their display order
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Cards List */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {orderedCards.length === 0 ? (
            <div className="text-center py-12">
              <Type className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-700" />
              <p className="text-gray-500 dark:text-gray-400">No cards to reorder</p>
            </div>
          ) : orderedCards.length === 1 ? (
            <div className="text-center py-12">
              <Type className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-700" />
              <p className="text-gray-500 dark:text-gray-400">You need at least 2 cards to reorder</p>
              <div className="mt-6 max-w-md mx-auto">
                <SortableCardItem card={orderedCards[0]} index={0} />
              </div>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={orderedCards.map((card) => card.id_v2)}
                strategy={verticalListSortingStrategy}
              >
                {orderedCards.map((card, index) => (
                  <SortableCardItem key={card.id_v2} card={card} index={index} />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleReset}
              disabled={!hasChanges}
              className="flex items-center space-x-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Reset to original order"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            {hasChanges && (
              <span className="text-sm text-amber-600 dark:text-amber-400">
                Unsaved changes
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCancel}
              className="px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className="flex items-center space-x-2 px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>Save Order</span>
            </button>
          </div>
        </div>

        {/* Info Notice */}
        <div className="px-6 pb-6 pt-0">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Note:</strong> The new order will be saved and synchronized across all your devices.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardOrderManager;
